import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { usePostHog } from '@lib/utils/posthog';
import { trackEvent } from "@lib/utils/marketingTracking";
import SeriesAILogo from "../SeriesAILogo/SeriesAILogo";
import HeroIcon from "@assets/images/atticus/icons/hero-icon.svg";
import ArrowDown from "@assets/images/atticus/icons/ArrowDown.svg";
import { Container, Nav, Navbar, NavDropdown } from "react-bootstrap";
import "./NavBar.css";

const NavBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPracticeOpen, setIsPracticeOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const user = useSelector((state) => state?.user);
  const posthog = usePostHog();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const focusMainContent = () => {
    // Some layouts (the law library) name their landmark differently.
    const mainContent = document.getElementById("main-content") || document.querySelector("main");
    if (!mainContent) return;
    mainContent.setAttribute("tabindex", "-1");
    mainContent.focus();
  };

  const getUrlGroup = () => {
    if (pathname.startsWith('/law')) return 'law';
    if (pathname.startsWith('/cite-checker')) return 'cite-checker';
    if (pathname.startsWith('/cases')) return 'cases';
    return 'other';
  };

  // The newsletter form lives in the footer. Scroll to it when this page
  // renders one, otherwise send the user home with the hash — NewsletterBar
  // scrolls itself into view when it mounts on a #newsletter URL.
  const handleNewsletterClick = () => {
    setIsMenuOpen(false);

    const target = document.getElementById("newsletter");
    if (!target) {
      navigate("/#newsletter");
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    document.getElementById("newsletter-email-input")?.focus({ preventScroll: true });
  };

  const trackSignupClick = (componentDetail) => {
    const urlGroup = getUrlGroup();
    const source = {
      "cite-checker": "cite_checker",
      cases: "cases",
      law: "law",
    }[urlGroup] || "public_page";
    trackEvent("sign_up_cta_click", { source, placement: componentDetail });
    posthog?.capture('click-conv-top-funnel', {
      url: window.location.href,
      component_detail: componentDetail,
      'url-group': urlGroup,
    });
  };

  return (
    <>
      {/* Every public page renders this nav, so the skip link lives here
         rather than in one layout: that is what gets keyboard users past
         the menu on pages Home/_Layout does not wrap. */}
      <a className="skip-link" href="#main-content" onClick={focusMainContent}>Skip to main content</a>
      <Navbar expand="lg" className="py-3 header" expanded={isMenuOpen}>
        <Container fluid className="nav-content">
          <NavLink to="/" className="d-flex align-items-center nav-logo-link" aria-label="Nougat homepage">
            <SeriesAILogo className="nav-logo-animate" />
          </NavLink>

          <Navbar.Toggle
            aria-controls="navbar-nav"
            aria-expanded={isMenuOpen}
            label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <span className="navbar-toggler-icon-custom">x</span>
            ) : (
              <span className="navbar-toggler-icon"></span>
            )}
          </Navbar.Toggle>

          <Navbar.Collapse id="navbar-nav" className="justify-content-between">
            <Nav className="mx-auto nav-menu-items">
              <NavLink to="/" className="nav-menu-item nav-link" onClick={() => setIsMenuOpen(false)}>Home</NavLink>
              <NavDropdown
                className="navbar-pricing-dropdown"
                title={<span className="custom-dropdown-icon-container"><span>Practice Areas</span><img src={ArrowDown} alt="" className={`custom-dropdown-icon ${isPracticeOpen ? "rotated" : ""}`} width="11" height="6" /></span>}
                id="practice-dropdown"
                onToggle={(isOpen) => setIsPracticeOpen(isOpen)}
              >
                <NavLink to="/products" className="dropdown-item nav-menu-item" onClick={() => setIsMenuOpen(false)}>All</NavLink>
                <NavLink to="/civil" className="dropdown-item nav-menu-item" onClick={() => setIsMenuOpen(false)}>Civil</NavLink>
                <NavLink to="/criminal" className="dropdown-item nav-menu-item" onClick={() => setIsMenuOpen(false)}>Criminal</NavLink>
              <NavLink to="/startups-vc" className="dropdown-item nav-menu-item" onClick={() => setIsMenuOpen(false)}>Startups &amp; VC</NavLink>
              </NavDropdown>
              <NavDropdown
                className="navbar-pricing-dropdown"
                title={<span className="custom-dropdown-icon-container"><span>Pricing</span><img src={ArrowDown} alt="" className={`custom-dropdown-icon ${isPricingOpen ? "rotated" : ""}`} width="11" height="6" /></span>}
                id="pricing-dropdown"
                onToggle={(isOpen) => setIsPricingOpen(isOpen)}
              >
                <NavLink to="/pricing" className="dropdown-item nav-menu-item" onClick={() => setIsMenuOpen(false)}>Pricing Plans</NavLink>
                <NavLink to="/small-firm" className="dropdown-item nav-menu-item" onClick={() => setIsMenuOpen(false)}>Try One Matter</NavLink>
                <NavLink to="/big-firm" className="dropdown-item nav-menu-item" onClick={() => setIsMenuOpen(false)}>Ready to Commit</NavLink>
                <NavLink to="/partnerships" className="dropdown-item nav-menu-item" onClick={() => setIsMenuOpen(false)}>Partnerships</NavLink>
              </NavDropdown>
              <NavLink to="/blog" className="nav-menu-item nav-link" onClick={() => setIsMenuOpen(false)}>Blog</NavLink>
              <NavLink to="/cite-checker" className="nav-menu-item nav-link" onClick={() => setIsMenuOpen(false)}>Free Cite-Checker</NavLink>
              <NavLink to="/law" className="nav-menu-item nav-link" onClick={() => setIsMenuOpen(false)}>Free Law Research</NavLink>
              <button type="button" className="nav-menu-item nav-link nav-newsletter-button" onClick={handleNewsletterClick}>Newsletter</button>
            </Nav>

            <div className="d-flex align-items-center nav-buttons-container flex-wrap">
              {user ? (
                <>
                  <Link to="/dashboard/2" className="common-button button--outline-new" onClick={() => setIsMenuOpen(false)}>Dashboard</Link>
                  <Link to="/logout" className="common-button button--primary-new" onClick={() => setIsMenuOpen(false)}>Log out</Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="common-button button--outline-new" onClick={() => setIsMenuOpen(false)}>Log in</Link>
                  <Link to="/signup" className="common-button button--primary-new" onClick={() => { trackSignupClick(getUrlGroup() === 'cite-checker' ? 'navbar-signup-text' : 'navbar-signup'); setIsMenuOpen(false); }}>Sign Up</Link>
                </>
              )}
              <Link to="/signup" aria-label="Sign up for Nougat" className="button-circle common-button button--circle-primary d-flex align-items-center justify-content-center" onClick={() => trackSignupClick(getUrlGroup() === 'cite-checker' ? 'navbar-signup-arrow' : 'navbar-signup-icon')}>
                <img src={HeroIcon} className="nav-circle-btn-icon" alt="" width="18" height="18" />
              </Link>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </>
  );
};

export default NavBar;

